import { useCallback, useEffect, useState } from 'react';

export const RAIL_MIN_WIDTH = 340;
export const RAIL_MAX_WIDTH = 720;
export const RAIL_DEFAULT_WIDTH = 400;
const STORAGE_KEY = 'mj-rail-width';
const KEYBOARD_STEP = 24;

// `cap` (the header panel's share of the viewport) wins over the minimum on a small window.
const clampWidth = (n: number, cap = RAIL_MAX_WIDTH) =>
  Math.min(cap, RAIL_MAX_WIDTH, Math.max(RAIL_MIN_WIDTH, Math.round(n)));

function getStoredWidth(cap?: number): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const w = Number.parseInt(stored, 10);
      if (Number.isFinite(w)) return clampWidth(w, cap);
    }
  } catch {
    /* storage unavailable — fall through to default */
  }
  return clampWidth(RAIL_DEFAULT_WIDTH, cap);
}

export interface RailResize {
  width: number;
  dragging: boolean;
  /** Begin a pointer drag from the panel's left-edge separator. */
  startDrag: () => void;
  /** Keyboard resize on the separator: ArrowLeft widens, ArrowRight narrows. */
  onSeparatorKeyDown: (event: React.KeyboardEvent) => void;
  min: number;
  max: number;
}

/**
 * Width state for the MJ rail: persisted to localStorage, resized by dragging
 * the panel's left edge (the rail's right edge is the viewport's right edge) or
 * with the arrow keys while the separator is focused. Copied — deliberately not
 * imported — from EditorChatPanel's hand-rolled pattern so the two surfaces stay
 * independently owned.
 */
export function useRailResize(maxWidth?: number): RailResize {
  const [raw, setWidth] = useState<number>(() => getStoredWidth(maxWidth));
  // Clamped on every read, so a window that shrinks under a stored width narrows the panel (and the stored value).
  const width = clampWidth(raw, maxWidth);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => setWidth(clampWidth(window.innerWidth - e.clientX, maxWidth));
    const onUp = () => setDragging(false);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, maxWidth]);

  useEffect(() => {
    if (dragging) return;
    try {
      localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [width, dragging]);

  const startDrag = useCallback(() => setDragging(true), []);

  const onSeparatorKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setWidth((w) => clampWidth(w + KEYBOARD_STEP, maxWidth));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setWidth((w) => clampWidth(w - KEYBOARD_STEP, maxWidth));
    }
  }, [maxWidth]);

  return {
    width,
    dragging,
    startDrag,
    onSeparatorKeyDown,
    min: RAIL_MIN_WIDTH,
    max: Math.min(RAIL_MAX_WIDTH, maxWidth ?? RAIL_MAX_WIDTH),
  };
}
